import { useEffect, useRef, useState } from 'react';
import {
  MessageSquare,
  LogOut,
  Settings,
  Plus,
  History,
  MoreVertical,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { ConversationHistory } from '@/components/chat/ConversationHistory';
import { SettingsPanel } from '@/components/settings/SettingsPanel';
import logoUrl from '@/assets/logo.png';

// Ira Autopilot — taskpane surface. Header keeps only the brand,
// the active tab, and a single overflow menu so the layout stays
// clean on narrow Excel taskpanes (~320px). Heavy actions
// (history, new chat, settings, sign out) live inside the dropdown.
type Tab = 'chat' | 'settings';

export function MainLayout() {
  const [tab, setTab] = useState<Tab>('chat');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthStore();
  const { streaming, resetConversation } = useChatStore();

  // Dismiss dropdown on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const newChat = () => {
    setMenuOpen(false);
    resetConversation();
    setTab('chat');
  };

  const openHistory = () => {
    setMenuOpen(false);
    setHistoryOpen(true);
  };

  const openSettings = () => {
    setMenuOpen(false);
    setTab('settings');
  };

  const openChat = () => {
    setMenuOpen(false);
    setTab('chat');
  };

  const requestSignOut = () => {
    setMenuOpen(false);
    setConfirmSignOut(true);
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-border bg-white px-3 py-2 dark:bg-surface-dark dark:border-border-dark">
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={logoUrl}
            alt="Ira Skills"
            className="h-6 w-auto max-w-[140px] object-contain"
          />
        </div>
        <div className="flex items-center gap-1">
          <TabButton
            label="Chat"
            icon={<MessageSquare className="h-4 w-4" />}
            active={tab === 'chat'}
            onClick={openChat}
          />
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              title="Menu"
              aria-label="Menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="btn-ghost p-2"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-md border border-border bg-white py-1 shadow-lg dark:border-border-dark dark:bg-surface-dark"
              >
                <MenuItem
                  icon={<Plus className="h-4 w-4" />}
                  label="New chat"
                  disabled={streaming}
                  onClick={newChat}
                />
                <MenuItem
                  icon={<History className="h-4 w-4" />}
                  label="History"
                  disabled={streaming}
                  onClick={openHistory}
                />
                <MenuItem
                  icon={<Settings className="h-4 w-4" />}
                  label="Settings"
                  onClick={openSettings}
                />
                <div className="my-1 border-t border-border dark:border-border-dark" />
                <MenuItem
                  icon={<LogOut className="h-4 w-4" />}
                  label="Sign out"
                  onClick={requestSignOut}
                  destructive
                />
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden">
        {tab === 'chat' && <ChatInterface />}
        {tab === 'settings' && <SettingsPanel onBack={() => setTab('chat')} />}
      </main>

      <ConversationHistory open={historyOpen} onClose={() => setHistoryOpen(false)} />

      {confirmSignOut && (
        <SignOutDialog
          email={user?.email}
          onCancel={() => setConfirmSignOut(false)}
          onConfirm={() => {
            setConfirmSignOut(false);
            logout();
          }}
        />
      )}
    </div>
  );
}

function TabButton({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'btn-ghost gap-1.5 px-2 py-1 text-xs',
        active ? 'bg-primary/10 text-primary hover:bg-primary/10' : '',
      ].join(' ')}
    >
      {icon}
      {label}
    </button>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={[
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition disabled:opacity-50',
        destructive
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10'
          : 'text-text-primary hover:bg-black/5 dark:text-text-primary-dark dark:hover:bg-white/5',
      ].join(' ')}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function SignOutDialog({
  email,
  onCancel,
  onConfirm,
}: {
  email?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Escape key cancels; Enter confirms.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, onConfirm]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signout-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-border bg-white p-4 shadow-xl dark:border-border-dark dark:bg-surface-dark"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="signout-title"
          className="text-base font-semibold text-text-primary dark:text-text-primary-dark"
        >
          Sign out?
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {email
            ? `You'll be signed out of ${email}. Continue?`
            : "You'll be signed out of this session. Continue?"}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-black/5 dark:border-border-dark dark:hover:bg-white/5"
            autoFocus
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
